import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { api } from '../utils/api';
import { Plus, Upload, Download, Pencil, Trash2, Search, PackageCheck } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { useWebSocketContext } from '../context/WebSocketContext';

export const Products = () => {
  const { user } = useAuth();
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [stockAdjust, setStockAdjust] = useState({ stock: '', box_stock: '', unit_stock: '', reason: '' });
  const [adjustingStock, setAdjustingStock] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    barcode: '',
    cost: '',
    min_stock: '',
    max_stock: '',
    initial_stock: '',
    has_box: false,
    box_barcode: '',
    unit_barcode: '',
    units_per_box: '',
    initial_box_stock: '',
    initial_unit_stock: ''
  });
  const { subscribe } = useWebSocketContext();

  const fetchProducts = React.useCallback(async () => {
    try {
      const response = await api.getProducts();
      setProducts(response.data);
      setFilteredProducts(response.data);
    } catch (error) {
      toast.error('Error al cargar productos');
    }
  }, []);

  // Carga inicial
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // POLLING DESACTIVADO - causaba recargas visuales y pérdida de foco
  // useEffect(() => {
  //   const pollingInterval = setInterval(() => {
  //     fetchProducts();
  //   }, 5000);
  //   return () => clearInterval(pollingInterval);
  // }, [fetchProducts]);

  useEffect(() => {
    const filtered = products.filter(p => 
      p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.barcode.includes(searchTerm)
    );
    setFilteredProducts(filtered);
  }, [searchTerm, products]);

  // Subscribe to WebSocket updates (instantáneo)
  useEffect(() => {
    const unsubscribe = subscribe((message) => {
      if (message.type === 'stock_update' && message.product) {
        console.log('📦 Products: Actualización WebSocket recibida', message);
        
        // Actualizar el producto en el estado local sin recargar todo
        const updatedProduct = message.product;
        
        setProducts(prevProducts => {
          const index = prevProducts.findIndex(p => p.id === updatedProduct.id);
          if (index !== -1) {
            const newProducts = [...prevProducts];
            newProducts[index] = updatedProduct;
            return newProducts;
          }
          return prevProducts;
        });
        
        setFilteredProducts(prevProducts => {
          const index = prevProducts.findIndex(p => p.id === updatedProduct.id);
          if (index !== -1) {
            const newProducts = [...prevProducts];
            newProducts[index] = updatedProduct;
            return newProducts;
          }
          return prevProducts;
        });
        
        // Mostrar toast con el cambio
        const movementType = message.movement?.type === 'entry' ? 'Entrada' : 'Salida';
        toast.info(`${movementType}: ${updatedProduct.name}`, {
          description: `Stock: ${updatedProduct.stock} | Coste medio: €${updatedProduct.average_cost || 0}`,
          duration: 2000
        });
      }
    });

    return unsubscribe;
  }, [subscribe]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const data = {
        name: formData.name,
        barcode: formData.barcode,
        price: 0,  // Valor por defecto, no se usa
        cost: parseFloat(formData.cost),
        min_stock: parseInt(formData.min_stock),
        max_stock: formData.max_stock ? parseInt(formData.max_stock) : null
      };

      // Campos para productos con caja
      if (formData.has_box) {
        data.has_box = true;
        data.box_barcode = formData.box_barcode;
        data.unit_barcode = formData.unit_barcode;
        data.units_per_box = parseInt(formData.units_per_box);
        
        // Stock inicial para productos con caja (solo al crear)
        if (!editingProduct) {
          data.initial_box_stock = formData.initial_box_stock ? parseInt(formData.initial_box_stock) : 0;
          data.initial_unit_stock = formData.initial_unit_stock ? parseInt(formData.initial_unit_stock) : 0;
        }
      }

      if (editingProduct) {
        await api.updateProduct(editingProduct.id, data);
        toast.success('Producto actualizado');
      } else {
        // Add initial_stock only for new products WITHOUT box
        if (!formData.has_box) {
          data.initial_stock = formData.initial_stock ? parseInt(formData.initial_stock) : 0;
        }
        
        await api.createProduct(data);
        
        // Mensaje de éxito según tipo de producto
        if (formData.has_box) {
          const boxStock = data.initial_box_stock || 0;
          const unitStock = data.initial_unit_stock || 0;
          const totalUnits = (boxStock * data.units_per_box) + unitStock;
          if (totalUnits > 0) {
            toast.success(`Producto creado: ${boxStock} cajas + ${unitStock} uds sueltas (${totalUnits} uds totales)`);
          } else {
            toast.success('Producto creado (sin stock inicial)');
          }
        } else if (data.initial_stock > 0) {
          toast.success(`Producto creado con stock inicial de ${data.initial_stock} unidades`);
        } else {
          toast.success('Producto creado');
        }
      }

      setDialogOpen(false);
      resetForm();
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar producto');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Estás seguro de eliminar este producto?')) return;
    try {
      await api.deleteProduct(id);
      toast.success('Producto eliminado');
      fetchProducts();
    } catch (error) {
      toast.error('Error al eliminar producto');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      barcode: product.barcode,
      cost: product.cost.toString(),
      min_stock: product.min_stock.toString(),
      max_stock: product.max_stock?.toString() || '',
      initial_stock: '',
      has_box: product.has_box || false,
      box_barcode: product.box_barcode || '',
      unit_barcode: product.unit_barcode || '',
      units_per_box: product.units_per_box?.toString() || '',
      initial_box_stock: '',
      initial_unit_stock: ''
    });
    // Inicializar valores de ajuste de stock
    setStockAdjust({
      stock: product.stock?.toString() || '0',
      box_stock: product.box_stock?.toString() || '0',
      unit_stock: product.unit_stock?.toString() || '0',
      reason: ''
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      barcode: '',
      cost: '',
      min_stock: '',
      max_stock: '',
      initial_stock: '',
      has_box: false,
      box_barcode: '',
      unit_barcode: '',
      units_per_box: '',
      initial_box_stock: '',
      initial_unit_stock: ''
    });
    setStockAdjust({ stock: '', box_stock: '', unit_stock: '', reason: '' });
    setEditingProduct(null);
  };

  const handleStockAdjust = async () => {
    if (!editingProduct) return;
    
    setAdjustingStock(true);
    try {
      const data = editingProduct.has_box
        ? { box_stock: parseInt(stockAdjust.box_stock) || 0, unit_stock: parseInt(stockAdjust.unit_stock) || 0, reason: stockAdjust.reason }
        : { stock: parseInt(stockAdjust.stock) || 0, reason: stockAdjust.reason };
      
      await api.adjustProductStock(editingProduct.id, data);
      toast.success('Stock ajustado correctamente');
      fetchProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ajustar stock');
    } finally {
      setAdjustingStock(false);
    }
  };

  const handleExport = async () => {
    try {
      const response = await api.exportProducts();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'productos.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Productos exportados');
    } catch (error) {
      toast.error('Error al exportar');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      await api.importProducts(file);
      toast.success('Productos importados');
      fetchProducts();
    } catch (error) {
      toast.error('Error al importar productos');
    }
  };

  const canModify = user?.role !== 'operator';

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white">Productos</h1>
          <p className="text-base text-slate-600 dark:text-slate-300 mt-2">Gestiona el catálogo de productos</p>
        </div>
        <div className="flex flex-wrap gap-3">
          {canModify && (
            <>
              <Button 
                variant="secondary" 
                size="default" 
                onClick={() => document.getElementById('import-file').click()}
                data-testid="import-button"
              >
                <Upload className="h-4 w-4 mr-2" />
                Importar
              </Button>
              <input
                id="import-file"
                type="file"
                accept=".xlsx,.xls"
                className="hidden"
                onChange={handleImport}
                data-testid="import-file-input"
              />
            </>
          )}
          <Button variant="secondary" size="default" onClick={handleExport} data-testid="export-button">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {canModify && (
            <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
              <DialogTrigger asChild>
                <Button variant="primary" size="default" data-testid="add-product-button">
                  <Plus className="h-5 w-5 mr-2" />
                  Nuevo Producto
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>{editingProduct ? 'Editar Producto' : 'Nuevo Producto'}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre *</Label>
                      <Input
                        id="name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        data-testid="product-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="barcode">Código de Barras *</Label>
                      <Input
                        id="barcode"
                        value={formData.barcode}
                        onChange={(e) => setFormData({...formData, barcode: e.target.value})}
                        required
                        data-testid="product-barcode-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="cost">Coste de Compra *</Label>
                      <Input
                        id="cost"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.cost}
                        onChange={(e) => setFormData({...formData, cost: e.target.value})}
                        onKeyDown={(e) => {if (e.key === '-' || e.key === 'e') e.preventDefault();}}
                        required
                        data-testid="product-cost-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="min_stock">Stock Mínimo *</Label>
                      <Input
                        id="min_stock"
                        type="number"
                        min="0"
                        value={formData.min_stock}
                        onChange={(e) => setFormData({...formData, min_stock: e.target.value})}
                        onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                        required
                        data-testid="product-min-stock-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="max_stock">Stock Máximo</Label>
                      <Input
                        id="max_stock"
                        type="number"
                        min="0"
                        value={formData.max_stock}
                        onChange={(e) => setFormData({...formData, max_stock: e.target.value})}
                        onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                        data-testid="product-max-stock-input"
                      />
                    </div>
                  </div>
                  
                  {/* Sección de Productos en Caja */}
                  <div className="border-t pt-4 mt-4">
                    <div className="space-y-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          id="has_box"
                          checked={formData.has_box}
                          onChange={(e) => setFormData({
                            ...formData, 
                            has_box: e.target.checked,
                            box_barcode: e.target.checked ? formData.box_barcode : '',
                            unit_barcode: e.target.checked ? formData.unit_barcode : '',
                            units_per_box: e.target.checked ? formData.units_per_box : '',
                            initial_box_stock: e.target.checked ? formData.initial_box_stock : '',
                            initial_unit_stock: e.target.checked ? formData.initial_unit_stock : ''
                          })}
                          className="h-4 w-4 rounded border-gray-300"
                          data-testid="product-has-box-checkbox"
                        />
                        <Label htmlFor="has_box" className="font-semibold cursor-pointer">
                          📦 Este producto se comercializa en caja
                        </Label>
                      </div>
                      
                      {formData.has_box && (
                        <div className="pl-6 space-y-4 border-l-2 border-blue-200 dark:border-blue-800">
                          <div className="bg-blue-50 dark:bg-blue-950/30 p-3 rounded-md mb-4">
                            <p className="text-sm text-blue-900 dark:text-blue-100">
                              <strong>Gestión de stock físico:</strong>
                              <br/>• <strong>Cajas cerradas</strong>: Cajas completas sin abrir
                              <br/>• <strong>Unidades sueltas</strong>: Unidades fuera de caja
                              <br/>• El sistema abrirá cajas automáticamente si faltan unidades al vender
                            </p>
                          </div>
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div className="space-y-2">
                              <Label htmlFor="box_barcode">Código de Barras de la Caja *</Label>
                              <Input
                                id="box_barcode"
                                value={formData.box_barcode}
                                onChange={(e) => setFormData({...formData, box_barcode: e.target.value})}
                                required={formData.has_box}
                                placeholder="Ej: 7891234567890"
                                data-testid="product-box-barcode-input"
                              />
                            </div>
                            <div className="space-y-2">
                              <Label htmlFor="unit_barcode">Código de Barras de la Unidad *</Label>
                              <Input
                                id="unit_barcode"
                                value={formData.unit_barcode}
                                onChange={(e) => setFormData({...formData, unit_barcode: e.target.value})}
                                required={formData.has_box}
                                placeholder="Ej: 7891234567891"
                                data-testid="product-unit-barcode-input"
                              />
                            </div>
                          </div>
                          
                          <div className="space-y-2">
                            <Label htmlFor="units_per_box">Unidades por Caja *</Label>
                            <Input
                              id="units_per_box"
                              type="number"
                              min="1"
                              value={formData.units_per_box}
                              onChange={(e) => setFormData({...formData, units_per_box: e.target.value})}
                              onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                              required={formData.has_box}
                              placeholder="Ej: 6"
                              className="max-w-xs"
                              data-testid="product-units-per-box-input"
                            />
                          </div>
                          
                          {/* Stock Inicial para productos con caja - Solo al crear */}
                          {!editingProduct && (
                            <div className="mt-4 p-4 bg-green-50 dark:bg-green-950/30 rounded-lg border border-green-200 dark:border-green-800">
                              <h4 className="font-semibold text-green-800 dark:text-green-200 mb-3">
                                Stock Inicial
                              </h4>
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div className="space-y-2">
                                  <Label htmlFor="initial_box_stock" className="text-green-700 dark:text-green-300">
                                    Cajas Cerradas Iniciales
                                  </Label>
                                  <Input
                                    id="initial_box_stock"
                                    type="number"
                                    min="0"
                                    value={formData.initial_box_stock}
                                    onChange={(e) => setFormData({...formData, initial_box_stock: e.target.value})}
                                    onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                                    placeholder="0"
                                    data-testid="product-initial-box-stock-input"
                                  />
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Cajas completas sin abrir
                                  </p>
                                </div>
                                <div className="space-y-2">
                                  <Label htmlFor="initial_unit_stock" className="text-green-700 dark:text-green-300">
                                    Unidades Sueltas Iniciales
                                  </Label>
                                  <Input
                                    id="initial_unit_stock"
                                    type="number"
                                    min="0"
                                    value={formData.initial_unit_stock}
                                    onChange={(e) => setFormData({...formData, initial_unit_stock: e.target.value})}
                                    onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                                    placeholder="0"
                                    data-testid="product-initial-unit-stock-input"
                                  />
                                  <p className="text-xs text-green-600 dark:text-green-400">
                                    Unidades fuera de caja
                                  </p>
                                </div>
                              </div>
                              {formData.units_per_box && (formData.initial_box_stock || formData.initial_unit_stock) && (
                                <div className="mt-3 p-2 bg-white dark:bg-slate-800 rounded text-sm">
                                  <span className="font-medium">Total unidades: </span>
                                  {((parseInt(formData.initial_box_stock) || 0) * (parseInt(formData.units_per_box) || 0)) + (parseInt(formData.initial_unit_stock) || 0)} uds
                                  <span className="text-muted-foreground ml-2">
                                    ({formData.initial_box_stock || 0} cajas × {formData.units_per_box} + {formData.initial_unit_stock || 0} sueltas)
                                  </span>
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {!formData.has_box && !editingProduct && (
                        <div className="space-y-2">
                          <Label htmlFor="initial_stock">Stock Inicial</Label>
                          <Input
                            id="initial_stock"
                            type="number"
                            min="0"
                            value={formData.initial_stock}
                            onChange={(e) => setFormData({...formData, initial_stock: e.target.value})}
                            onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                            placeholder="0"
                            data-testid="product-initial-stock-input"
                          />
                          <p className="text-xs text-muted-foreground">Solo al crear producto nuevo</p>
                        </div>
                      )}
                      
                      {/* Sección de Ajuste de Stock - Solo al editar */}
                      {editingProduct && (
                        <div className="mt-4 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
                          <div className="flex items-center gap-2 mb-3">
                            <PackageCheck className="h-5 w-5 text-amber-600" />
                            <h4 className="font-semibold text-amber-800 dark:text-amber-200">
                              Ajustar Stock Real
                            </h4>
                          </div>
                          <p className="text-xs text-amber-700 dark:text-amber-300 mb-3">
                            Modifica el stock actual del producto. Se creará un registro de ajuste.
                          </p>
                          
                          {formData.has_box ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                              <div className="space-y-2">
                                <Label className="text-amber-700 dark:text-amber-300">Cajas Cerradas</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={stockAdjust.box_stock}
                                  onChange={(e) => setStockAdjust({...stockAdjust, box_stock: e.target.value})}
                                  onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                                  className="bg-white dark:bg-slate-800"
                                />
                              </div>
                              <div className="space-y-2">
                                <Label className="text-amber-700 dark:text-amber-300">Unidades Sueltas</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={stockAdjust.unit_stock}
                                  onChange={(e) => setStockAdjust({...stockAdjust, unit_stock: e.target.value})}
                                  onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                                  className="bg-white dark:bg-slate-800"
                                />
                              </div>
                              <div className="col-span-2 text-sm text-amber-800 dark:text-amber-200">
                                <span className="font-medium">Total: </span>
                                {((parseInt(stockAdjust.box_stock) || 0) * (parseInt(formData.units_per_box) || 1)) + (parseInt(stockAdjust.unit_stock) || 0)} unidades
                              </div>
                            </div>
                          ) : (
                            <div className="space-y-2">
                              <Label className="text-amber-700 dark:text-amber-300">Stock Actual</Label>
                              <Input
                                type="number"
                                min="0"
                                value={stockAdjust.stock}
                                onChange={(e) => setStockAdjust({...stockAdjust, stock: e.target.value})}
                                onKeyDown={(e) => {if (e.key === '-' || e.key === 'e' || e.key === '.') e.preventDefault();}}
                                className="bg-white dark:bg-slate-800 max-w-xs"
                              />
                            </div>
                          )}
                          
                          <div className="mt-3 space-y-2">
                            <Label className="text-amber-700 dark:text-amber-300">Motivo del ajuste (opcional)</Label>
                            <Input
                              type="text"
                              value={stockAdjust.reason}
                              onChange={(e) => setStockAdjust({...stockAdjust, reason: e.target.value})}
                              placeholder="Ej: Corrección de inventario, Rotura..."
                              className="bg-white dark:bg-slate-800"
                            />
                          </div>
                          
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={handleStockAdjust}
                            disabled={adjustingStock}
                            className="mt-3 border-amber-500 text-amber-700 hover:bg-amber-100 dark:border-amber-600 dark:text-amber-300 dark:hover:bg-amber-900/30"
                          >
                            {adjustingStock ? 'Ajustando...' : 'Aplicar Ajuste de Stock'}
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex justify-end gap-3 mt-6">
                    <Button 
                      type="button" 
                      variant="secondary" 
                      size="default"
                      onClick={() => { setDialogOpen(false); resetForm(); }}
                    >
                      Cancelar
                    </Button>
                    <Button 
                      type="submit" 
                      variant="success"
                      size="default"
                      data-testid="save-product-button"
                    >
                      {editingProduct ? 'Actualizar' : 'Crear'}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <Card className="border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o código..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="max-w-sm rounded-sm"
              data-testid="search-input"
            />
          </div>
        </CardHeader>
        <CardContent>
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="uppercase tracking-wider text-xs">Nombre</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Código</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Stock</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Último Coste</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Coste Medio</TableHead>
                  <TableHead className="uppercase tracking-wider text-xs">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredProducts.map((product) => (
                  <TableRow key={product.id} className="hover:bg-slate-50 dark:hover:bg-slate-900">
                    <TableCell className="font-medium">
                      {product.name}
                      {product.has_box && (
                        <span className="ml-2 text-xs bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200 px-2 py-0.5 rounded">
                          📦 Caja
                        </span>
                      )}
                    </TableCell>
                    <TableCell className="font-mono">
                      <div className="space-y-1">
                        {product.has_box ? (
                          <>
                            <div className="text-xs text-muted-foreground">📦 Caja: {product.box_barcode}</div>
                            <div className="text-xs text-muted-foreground">🔢 Unidad: {product.unit_barcode || product.barcode}</div>
                          </>
                        ) : (
                          <div>{product.barcode}</div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">
                      <div className={product.stock <= product.min_stock ? 'text-red-600 dark:text-red-400 font-bold bg-red-50 dark:bg-red-950/30 px-2 py-1 rounded' : ''}>
                        {product.stock_info ? (
                          <>
                            <div className="font-bold">
                              {product.stock_info.box_stock} 📦 + {product.stock_info.unit_stock} uds
                            </div>
                            <div className="text-xs text-muted-foreground">
                              Total: {product.stock_info.total_units} uds
                            </div>
                          </>
                        ) : (
                          <>{product.stock} uds</>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-mono">€{product.last_cost ? product.last_cost.toFixed(2) : '-'}</TableCell>
                    <TableCell className="font-mono">€{product.average_cost.toFixed(2)}</TableCell>
                    <TableCell>
                      {canModify && (
                        <div className="flex gap-2">
                          <Button 
                            variant="secondary" 
                            size="icon-sm" 
                            onClick={() => handleEdit(product)} 
                            data-testid={`edit-product-${product.id}`}
                            className="hover:bg-blue-100 hover:text-blue-700"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="danger" 
                            size="icon-sm" 
                            onClick={() => handleDelete(product.id)} 
                            data-testid={`delete-product-${product.id}`}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-3">
            {filteredProducts.map((product) => (
              <div key={product.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg p-4 shadow-sm">
                <div className="flex justify-between items-start mb-3">
                  <div className="flex-1">
                    <h3 className="font-semibold text-base text-slate-900 dark:text-white">{product.name}</h3>
                    <p className="text-sm text-slate-500 dark:text-slate-400 font-mono">{product.barcode}</p>
                  </div>
                  {canModify && (
                    <div className="flex gap-2 ml-2">
                      <Button 
                        variant="secondary" 
                        size="icon-sm" 
                        onClick={() => handleEdit(product)} 
                        className="hover:bg-blue-100 hover:text-blue-700"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button 
                        variant="danger" 
                        size="icon-sm" 
                        onClick={() => handleDelete(product.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Stock</p>
                    <p className={`font-semibold font-mono ${product.stock <= product.min_stock ? 'text-red-600 dark:text-red-400' : 'text-slate-900 dark:text-white'}`}>
                      {product.stock}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Último Coste</p>
                    <p className="font-semibold font-mono text-slate-900 dark:text-white">€{product.last_cost ? product.last_cost.toFixed(2) : '-'}</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 dark:text-slate-400 uppercase">Coste Medio</p>
                    <p className="font-semibold font-mono text-slate-900 dark:text-white">€{product.average_cost.toFixed(2)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              No se encontraron productos
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};